import { VisitTypeEnum, endVisitOptions, startVisitOptions, type EndVisitCommand } from '@generated';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '~/util/http/useMutation';
import { getVisitRoutePath } from '../../pages/visits/utils';
import { useGeolocation, type GeolocationResult } from '~/shared/hooks/useGeolocation';
import { useQuery, type UseMutationResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { ProblemDetails } from '~/util/http/types';
import { useGetVisitOptions, useGetVisitStatusOptions } from './queries';

type useStartVisitResult = UseMutationResult<string, AxiosError<ProblemDetails, unknown>, unknown, unknown> & {
	handleStartVisit: (initiatingFrequencyIds: string[]) => void;
	geoLocation: GeolocationResult;
};

export function useStartVisit(stayId: string): useStartVisitResult {
	const navigate = useNavigate();
	const geoLocation = useGeolocation();

	const startVisit = useMutation<string>({
		...startVisitOptions,
		onSuccess(result, _, queryClient) {
			queryClient.removeQueries({ queryKey: [stayId] });
			navigate(getVisitRoutePath(stayId!, result));
		}
	});

	const handleStartVisit = (initiatingFrequencyIds: string[]) => {
		if (!geoLocation?.latitude || !geoLocation.longitude) {
			throw new Error('Unable to get geo coordinates');
		}
		if (initiatingFrequencyIds.length === 0) {
			throw new Error('At least one frequency visit ID must be provided to start an visit.');
		}
		startVisit.mutate({
			stayId,
			initiatingFrequencyIds,
			timeInLatitude: geoLocation.latitude,
			timeInLongitude: geoLocation.longitude,
			eVVOverrideCode: 'test',
			visitTypeId: VisitTypeEnum.InPerson
		});
	};
	return {
		handleStartVisit,
		...startVisit,
		geoLocation
	};
}

type useEndVisitResult = UseMutationResult<void, AxiosError<ProblemDetails, unknown>, EndVisitCommand, unknown> & {
	handleEndVisit: () => void;
	geoLocation: GeolocationResult;
};

export function useEndVisit(visitId: string): useEndVisitResult {
	const { refetch: refetchVisitStatus } = useQuery(useGetVisitStatusOptions(visitId));
	const { refetch: refetchVisit } = useQuery(useGetVisitOptions(visitId));
	const geoLocation = useGeolocation();
	const endVisit = useMutation<void, EndVisitCommand>(endVisitOptions);

	const handleEndVisit = () => {
		if (!geoLocation?.latitude || !geoLocation.longitude) {
			throw new Error('Unable to get geo coordinates');
		}
		endVisit.mutate(
			{
				visitId,
				timeOutLatitude: geoLocation.latitude,
				timeOutLongitude: geoLocation.longitude
			},
			{
				onSuccess: () => {
					refetchVisit();
					refetchVisitStatus();
				}
			}
		);
	};

	return {
		handleEndVisit,
		...endVisit,
		geoLocation
	};
}
